'use strict';

var CmdHttpRequest = require('./CmdHttpRequest');
var CmdSocket = require('./CmdSocket');
var CmdWebSocket = require('./CmdWebSocket');
var IdGenerator = require('./IdGenerator');
var Log = require('../../../../log/Log');
var StateMachine = require('../../state-machine.min');
var sutl = require('../../../../sutl/Sutl');

var log = Log.channel('CmdDevice');
var driverLog = Log.channel('CmdDevice', 'driver');

var logVerbose = false;

function CmdDevice(ipAddress, username, password, onConnectStatus,
    onControlStatus, config) {
    this.ipAddress = ipAddress;
    this.username = username;
    this.password = password;
    this.onConnectStatus = onConnectStatus;
    this.onControlStatus = onControlStatus;

    this.busy = false;
    this.busyTimeout = null;
    this.connectRetryTimeout = null;
    this.connectState = 'connectFailed';
    this.cmdHttpRequest = null;
    this.cmdSocket = null;
    this.cmdWebSocket = null;
    this.deferTimeouts = [];
    this.initializationTimeout = null;
    this.validationTimeout = null;
    this.pingAttempts = 0;
    this.pingReceived = false;
    this.pingSuspended = false;
    this.pingTimeout = null;
    this.pollTimeout = null;
    this.simConnectTimeout = null;
    this.suspendSyncTimeout = null;
    this.validateRepeatIndex = 0;
    this.validateStepIndex = 0;

    this.config = { dummy: 'dummy' };

    this.caps = JSON.parse(JSON.stringify(this.capsTemplate));

    this.fsm = StateMachine.create({
        events: [
            { name: 'startup',        from:   'none',              to: 'disconnected' },
            { name: 'connect',        from: [ 'disconnected',
                                              'connectFailed',
                                              'authFailed',
                                              'verifyFailed',
                                              'connecting',
                                              'initializing',
                                              'validating',
                                              'controlling',
                                              'controlFailed' ],   to: 'connecting' },
            { name: 'failConnect',    from:   'connecting',        to: 'connectFailed' },
            { name: 'failAuth',       from:   'connecting',        to: 'authFailed' },
            { name: 'failVerify',     from:   'connecting',        to: 'verifyFailed' },
            { name: 'retryConnect',   from: [ 'authFailed',
                                              'connectFailed',
                                              'verifyFailed' ],    to: 'connecting' },
            { name: 'initialize',     from: [ 'connecting',
                                              'controlling',
                                              'controlFailed' ],   to: 'initializing' },
            { name: 'validate',       from: [ 'initializing',
                                              'controlling',
                                              'controlFailed' ],   to: 'validating' },
            { name: 'control',        from:   'validating',        to: 'controlling' },
            { name: 'failValidate',   from: [ 'validating',
                                              'controlling' ],     to: 'controlFailed' },
            { name: 'failPing',       from: [ 'initializing',
                                              'validating',
                                              'controlling',
                                              'controlFailed' ],   to: 'connecting' },
        ],
        callbacks: {
            onenterstate: this.onEnterState.bind(this),
            onconnecting: this.onConnecting.bind(this),
            onconnectFailed: this.onConnectFailed.bind(this),
            onauthFailed: this.onAuthFailed.bind(this),
            onverifyFailed: this.onVerifyFailed.bind(this),
            oninitializing: this.onInitializing.bind(this),
            onvalidating: this.onValidating.bind(this),
            oncontrolling: this.onControlling.bind(this),
            oncontrolFailed: this.onControlFailed.bind(this),
        },
    });

    if (typeof this.ports !== 'undefined' && this.ports !== null) {
        // Sanity check to make sure the driver set the correct activeStates for
        // the ping command.

        if (!this.commands.hasOwnProperty('ping')) {
            log.error(this + ': create: no ping command defined');
        }

        if (!this.commands.ping.hasOwnProperty('activeStates') ||
            !this.commands.ping.activeStates.includes('controlling') ||
            !this.commands.ping.activeStates.includes('controlFailed')) {
            log.error(this + ': create: ping command does not have ' +
                'activeStates = [\'controlling\', \'controlFailed\']');
        }
    }

    this.fsm.startup();
    this.updateConfig(config);
}

CmdDevice.prototype.name = 'PI-14 Pseudoacoustic Infector';
CmdDevice.prototype.allowEmptyCommands = false;
CmdDevice.prototype.asyncHandlers = {};
CmdDevice.prototype.commands = {};
CmdDevice.prototype.connectRetryMs = 10000;
CmdDevice.prototype.defaultCallCanEndMeeting = false;
CmdDevice.prototype.httpRequestEnable = false;
CmdDevice.prototype.httpRequestBaseUrl = null;
CmdDevice.prototype.httpRequestDataFormat = 'json';
CmdDevice.prototype.initializationTimeoutMs = 10000;
CmdDevice.prototype.isAsync = true;
CmdDevice.prototype.isBinary = false;
CmdDevice.prototype.pingAttemptLimit = 2;
CmdDevice.prototype.pingMs = 3000;
CmdDevice.prototype.pollIntervalMs = 0;
CmdDevice.prototype.ports = null;
CmdDevice.prototype.receiveTerminators = [ '\r', '\n' ];
CmdDevice.prototype.rejectAsync = true;
CmdDevice.prototype.simulate = false;
CmdDevice.prototype.sendTerminator = '\r';
CmdDevice.prototype.syncTimeoutMs = 0;
CmdDevice.prototype.unterminatedRegExp = null;
CmdDevice.prototype.validationTimeoutMs = 0;
CmdDevice.prototype.webSocketEnable = false;
CmdDevice.prototype.webSocketPort = null;
CmdDevice.prototype.webSocketUrlPath = null;
CmdDevice.prototype.webSocketDataFormat = 'json'; // raw or json

CmdDevice.prototype.capsTemplate = {
    cameras: {},
    cameraPresets: {},
    disabledCalls: {
        pstn: 0,
        teams: 0,
        video: 0,
        voip: 0,
        zoom: 0,
    },
    hasBusy: false,
    hasCalendar: false,
    hasCameraTrackingUi: false,
    hasDirectory: false,
    hasDisplayBlank: false,
    hasDisplayBlankUi: false,
    hasDisplayFreeze: false,
    hasDisplayFreezeUi: false,
    hasDynamicVideoInputs: false,
    hasFirmwareVersion: false,
    hasLayoutSwitcher: false,
    hasRoomMute: false,
    hasRoomVolume: false,
    hasRoomVolumeMute: false,
    hasSerialNumber: false,
    hasPowerControl: false,
    hasVideoInputs: false,
    hasVideoOutputs: false,
    hasVideoSwitcher: false,
    hasVolume: false,
    hasVolumeMute: false,
    hasZoomConnector: false,
    isDisplayBlankStateless: false,
    isDisplayFreezeStateless: false,
    isMediaPlayerStateless: false,
    isPowerStateless: false,
    isPowerToggle: false,
    isRoomVolumeIncDec: false,
    isRoomVolumeStateless: false,
    isVideoInputStateless: false,
    isVolumeIncDec: false,
    isVolumeStateless: false,
    maxCalls: {
        pstn: 0,
        teams: 0,
        video: 0,
        voip: 0,
        zoom: 0,
    },
    videoInputs: {},
    videoInputsAreContent: false,
};

CmdDevice.prototype.connectStateMap = {
    'none': null,
    'disconnected': null,
    'connecting': 'connecting',
    'authFailed': 'authFailed',
    'connectFailed': 'connectFailed',
    'verifyFailed': 'connectFailed',
    'initializing': 'connecting',
    'validating': 'connecting',
    'controlling': 'online',
    'controlFailed': 'online',
};

// Function for sending status messages to EquipmentService.

CmdDevice.prototype.broadcastStatus = function(param, value) {
    // Use defer to prevent things from getting too deeply nested.
    this.defer(this.onControlStatus.bind(this, this, param, value));
};

// Internal function to determine if a command is active in the current state.

CmdDevice.prototype.commandIsActive = function(param) {
    if (!this.commands.hasOwnProperty(param)) return false;

    var cmd = this.commands[param];

    if (cmd.hasOwnProperty('activeStates')) {
        if (cmd.hasOwnProperty('activeValidateStates') &&
            this.fsm.current === 'validating') {
            var validateState = this.validateSteps[this.validateStepIndex].state;
            return cmd.activeValidateStates.includes(validateState);
        }

        if (cmd.hasOwnProperty('activeInitializeStates') &&
            this.fsm.current === 'initializing') {
            var initializeState = this.initializeSteps[this.initializeStepIndex].state;
            return cmd.activeInitializeStates.includes(initializeState);
        }

        return cmd.activeStates.includes(this.fsm.current);
    }

    return this.fsm.current === 'controlling';
};

// Helper function to compute assistive listening caps for drivers.

CmdDevice.prototype.computeAssistiveListeningCaps = function(caps) {
    if (this.config.hasOwnProperty('assistiveListening')) { // old schema, before SNTY-1597
        if (!this.config.assistiveListening.hasOwnProperty('mute') &&
            !this.config.assistiveListening.hasOwnProperty('volume')) {
            caps.assistiveListening = {
                hasMute: true,
                hasVolume: false,
            };
        } else { // new schema, after SNTY-1597
            caps.assistiveListening = {
                hasMute: this.config.assistiveListening.hasOwnProperty('mute'),
                hasVolume: this.config.assistiveListening.hasOwnProperty('volume'),
            };
        }
    }
};

// Helper function to compute audio input source caps for drivers.

CmdDevice.prototype.computeAudioInputSourceCaps = function(caps) {
    if (this.config.hasOwnProperty('audioInputSources')) {
        caps.audioInputSources = {};

        for (var srcId in this.config.audioInputSources) {
            if (!this.config.audioInputSources.hasOwnProperty(srcId)) continue;

            var src = this.config.audioInputSources[srcId];
            var props = [ 'description', 'icon', 'index', 'name' ];

            caps.audioInputSources[srcId] = {
                hasMute: src.hasOwnProperty('mute'),
                hasVolume: src.hasOwnProperty('volume'),
            };

            // Only copy properties that are defined so we don't end up with
            // properties with undefined values in the caps.

            for (var i = 0; i < props.length; i++) {
                if (src.hasOwnProperty(props[i])) {
                    caps.audioInputSources[srcId][props[i]] = src[props[i]];
                }
            }
        }
    }
};

// Helper function to compute camera presets caps for drivers.

CmdDevice.prototype.computeCameraPresetsCaps = function(caps) {
    var presetId;
    var preset;

    // Normal camera presets.

    if (this.config.hasOwnProperty('cameraPresets')) {
        for (presetId in this.config.cameraPresets) {
            if (!this.config.cameraPresets.hasOwnProperty(presetId)) continue;

            preset = this.config.cameraPresets[presetId];

            caps.cameraPresets[presetId] = {
                name: preset.name,
                icon: preset.icon,
                index: preset.index,
            };

            if (preset.hasOwnProperty('description')) {
                caps.cameraPresets[presetId].description = preset.description;
            }
        }
    }

    // Visca camera presets.

    if (this.config.hasOwnProperty('cameras')) {
        for (var configId in this.config.cameras) {
            if (!this.config.cameras.hasOwnProperty(configId)) continue;
            if (!this.config.cameras[configId].hasOwnProperty('presets')) continue;

            var presets = this.config.cameras[configId].presets;

            for (presetId in presets) {
                if (!presets.hasOwnProperty(presetId)) continue;

                preset = presets[presetId];

                caps.cameraPresets[presetId] = {
                    name: preset.name,
                    icon: preset.icon,
                    index: preset.index,
                };

                if (preset.hasOwnProperty('description')) {
                    caps.cameraPresets[presetId].description = preset.description;
                }
            }
        }
    }
};

// Internal function called to compute caps. Drivers must implement
// onComputeCaps() to compute and return caps no matter what state the driver
// is in. Caps should be computed as if the configuration were completely valid.
// Drivers can assume that the base class has propulated the config property
// before calling this function.

CmdDevice.prototype.computeCaps = function() {
    if (typeof this.onComputeCaps === 'undefined') {
        log.error(this + ': driver does not have onComputeCaps');
        return;
    }

    var newCaps = this.onComputeCaps();

    if (this.objEquals(newCaps, this.caps)) return;

    this.caps = newCaps;
    this.broadcastStatus('caps', this.caps);
};

// Helper function to compute caps from the config.

CmdDevice.prototype.computeCapsFromConfig = function(caps) {
    caps.hasRoomMute = this.config.hasOwnProperty('mics');
    caps.hasRoomVolume = this.config.hasOwnProperty('amplifier');
    caps.hasZoomConnector = sutl.getNode(this.config, 'zoomConnectorEnabled', false);
    caps.hasDisplayBlankUi = caps.hasDisplayBlank &&
        sutl.getNode(this.config, 'displayBlankUiEnabled', true);
    caps.hasDisplayFreezeUi = caps.hasDisplayFreeze &&
        sutl.getNode(this.config, 'displayFreezeUiEnabled', true);

    this.computeAssistiveListeningCaps(caps);
    this.computeAudioInputSourceCaps(caps);
    this.computeOverflowRoomCaps(caps);
    this.computeSoundReinforcementCaps(caps);

    if (caps.hasVideoSwitcher) {
        if (this.config.hasOwnProperty('videoInputs')) {
            if (this.config.hasOwnProperty('videoOutputs')) {
                this.computeVideoMatrixCaps(caps);
            } else {
                this.computeVideoInputsCaps(caps);
            }
        } else {
            caps.hasVideoSwitcher = false;
        }
    }
};

// Helper function to compute overflow room caps for drivers.

CmdDevice.prototype.computeOverflowRoomCaps = function(caps) {
    if (this.config.hasOwnProperty('overflowRooms')) {
        caps.overflowRooms = {};

        for (var roomId in this.config.overflowRooms) {
            if (!this.config.overflowRooms.hasOwnProperty(roomId)) continue;

            var room = this.config.overflowRooms[roomId];

            caps.overflowRooms[roomId] = {
                hasMute: room.hasOwnProperty('mute'),
                hasVolume: room.hasOwnProperty('volume'),
                name: room.name,
            };
        }
    }
};

// Helper function to compute sound reinforcement caps for drivers.

CmdDevice.prototype.computeSoundReinforcementCaps = function(caps) {
    if (this.config.hasOwnProperty('soundReinforcement')) {
        caps.soundReinforcement = {};

        for (var zoneId in this.config.soundReinforcement) {
            if (!this.config.soundReinforcement.hasOwnProperty(zoneId)) continue;

            var zone = this.config.soundReinforcement[zoneId];

            caps.soundReinforcement[zoneId] = {
                hasMute: zone.hasOwnProperty('mute'),
                hasVolume: zone.hasOwnProperty('volume'),
                name: zone.name,
            };
        }
    }
};

// Helper function to compute video input sources for drivers.

CmdDevice.prototype.computeVideoInputsCaps = function(caps, videoInputsConfig) {
    if (typeof videoInputsConfig === 'undefined') {
        videoInputsConfig = this.config.videoInputs;
    }

    caps.hasVideoInputs = false;

    if (!videoInputsConfig) return;

    caps.hasVideoInputs = true;
    caps.videoInputs = JSON.parse(JSON.stringify(videoInputsConfig));

    if (caps.videoInputsAreContent) {
        this.noContentId = null;

        for (var id in videoInputsConfig) {
            if (!videoInputsConfig.hasOwnProperty(id)) continue;
            var src = videoInputsConfig[id];
            if (!src.hasOwnProperty('farContent') || !src.farContent) {
                this.noContentId = id;
                break;
            }
        }

        if (this.noContentId === null) {
            log.error(this + ': unable to find video conference entry in ' +
                'videoInputs: ' + JSON.stringify(videoInputsConfig, null, 4));
        }
    }
};

// Helper function to compute video matrix stuff for drivers.

CmdDevice.prototype.computeVideoMatrixCaps = function(caps) {
    if (!this.config.hasOwnProperty('videoInputs') ||
        !this.config.hasOwnProperty('videoOutputs')) {
        caps.hasVideoInputs = false;
        caps.hasVideoOutputs = false;
        return;
    }

    caps.hasVideoInputs = true;
    caps.hasVideoOutputs = true;
    caps.videoInputs = angular.copy(this.config.videoInputs);
    caps.videoOutputs = angular.copy(this.config.videoOutputs);
};

// Called by EquipmentService to start the connection.

CmdDevice.prototype.connect = function() {
    if (this.fsm.current !== 'disconnected') {
        log.error(this + ': connect called in state "' + this.fsm.current + '"');
    }

    this.fsm.connect();
};

// Internal function for deferring work to happen outside of the current
// function.

CmdDevice.prototype.defer = function(deferredFunction, delayMs) {
    if (typeof delayMs === 'undefined') delayMs = 1;
    this.deferTimeouts.push(window.setTimeout((function() {
        this.deferTimeouts.shift();
        deferredFunction();
    }).bind(this), delayMs));
};

// Called by EquipmentService to destroy the driver. Drivers may add their own
// behavior by implementing onDestroy.

CmdDevice.prototype.destroy = function() {
    this.resetEverything();

    if (typeof this.onDestroy !== 'undefined') {
        this.onDestroy();
    }
};

// Internal function to disconnect any open connections to the device.

CmdDevice.prototype.disconnect = function() {
    if (this.cmdHttpRequest) {
        this.cmdHttpRequest.destroy();
    }

    if (this.cmdSocket !== null) {
        this.cmdSocket.destroy();
    }

    if (this.cmdWebSocket !== null) {
        this.cmdWebSocket.destroy();
    }
};

// Function that drivers can use to start the next step of the initializer. The
// startInitializer() function must be called to start the first step.

CmdDevice.prototype.doNextInitializeStep = function() {
    var step;
    var waitForNextStep;


    do {
        this.resetInitializationTimeout();

        step = this.initializeSteps[this.initializeStepIndex];

        if (this.initializerFirstStep) {
            this.initializerFirstStep = false;
        } else {
            if (step.hasOwnProperty('getRepeatCount')) {
                this.initializeRepeatIndex++;

                if (this.initializeRepeatIndex >= step.getRepeatCount.call(this)) {
                    this.initializeStepIndex++;
                    this.initializeRepeatIndex = 0;
                }
            } else {
                this.initializeStepIndex++;
                this.initializeRepeatIndex = 0;
            }
        }

        if (this.initializeStepIndex >= this.initializeSteps.length) {
            log.error(this + ': initializeStepIndex ' + this.initializeStepIndex +
                ' beyond bounds of steps array length ' +
                this.initializeSteps.length);
            return;
        }

        step = this.initializeSteps[this.initializeStepIndex];

        if (step.hasOwnProperty('getRepeatCount') &&
            step.getRepeatCount.call(this) <= 0) {
            if (logVerbose) log.debug(this + ': doNextInitializeStep: ' +
                step.state + ': skipping because repeat count is 0');
            waitForNextStep = false;
        } else {
            if (logVerbose) log.debug(this + ': doNextInitializeStep: ' +
                step.state + (step.hasOwnProperty('getRepeatCount') ?
                 ' (' + this.initializeRepeatIndex + '/' +
                 step.getRepeatCount.call(this) + ')' : ''));

            if (typeof step.action === 'string') {
                this.query(step.action);
                waitForNextStep = true;
            } else {
                waitForNextStep = step.action.call(this);

                if (waitForNextStep !== true && waitForNextStep !== false) {
                    log.error(this + ': ' + step.state + ' did not return boolean');
                }
            }
        }

    } while (!waitForNextStep);
};

// Function that drivers can use to start the next step of the validator. The
// startValidator() function must be called to start the first step.

CmdDevice.prototype.doNextValidateStep = function() {
    var step;
    var waitForNextStep;

    do {
       step = this.validateSteps[this.validateStepIndex];

        if (this.validatorFirstStep) {
            this.validatorFirstStep = false;
        } else {

            if (step.hasOwnProperty('getRepeatCount')) {
                this.validateRepeatIndex++;

                if (this.validateRepeatIndex >= step.getRepeatCount.call(this)) {
                    this.validateStepIndex++;
                    this.validateRepeatIndex = 0;
                }
            } else {
                this.validateStepIndex++;
                this.validateRepeatIndex = 0;
            }
        }

        if (this.validateStepIndex >= this.validateSteps.length) {
            log.error(this + ': validateStepIndex ' + this.validateStepIndex +
                ' beyond bounds of steps array length ' +
                this.validateSteps.length);
            return;
        }

        step = this.validateSteps[this.validateStepIndex];

        if (step.hasOwnProperty('getRepeatCount') &&
            step.getRepeatCount.call(this) <= 0) {
            if (logVerbose) log.debug(this + ': doNextValidateStep: ' +
                step.state + ': skipping because repeat count is 0');
            waitForNextStep = false;
        } else {
            if (logVerbose) log.debug(this + ': doNextValidateStep: ' +
                step.state + (step.hasOwnProperty('getRepeatCount') ?
                 ' (' + this.validateRepeatIndex + '/' +
                 step.getRepeatCount.call(this) + ')' : ''));

            if (typeof step.action === 'string') {
                this.query(step.action);
                waitForNextStep = true;
            } else {
                waitForNextStep = step.action.call(this);

                if (waitForNextStep !== true && waitForNextStep !== false) {
                    log.error(this + ': ' + step.state + ' did not return boolean');
                }
            }
        }

    } while (!waitForNextStep);
};

// Helper function for determining if a duplicate channel exists. As an example,
// consider a driver validating telephony control points testing the item
// at key 'a2' with control point 'controlStatus':
//
// duplicateExists('a2', this.config.telephony, 'controlStatus')

CmdDevice.prototype.duplicateExists = function(objKey, objs, prop) {
    var obj = objs[objKey];
    if (typeof prop !== 'undefined' && prop !== null) obj = obj[prop];

    for (var key in objs) {
        if (!objs.hasOwnProperty(key)) continue;

        var xobj = objs[key];
        if (typeof prop !== 'undefined' && prop !== null) xobj = xobj[prop];

        if (key !== objKey &&
            typeof xobj !== 'undefined' &&
            this.simulationParams.channelsAreEqual(xobj, obj)) {
            return true;
        }
    }

    return false;
};

CmdDevice.prototype.failInitialization = function() {
    this.resetEverything();
    this.fsm.connect();
};

// If a driver implements onConnect, it must call this function when it is
// finished with the onConnect behavior.

CmdDevice.prototype.finishConnecting = function() {
    var initializeHandled = false;

    if (this.simulate) {
        this.simConnectTimeout = window.setTimeout((function() {
            this.fsm.initialize();
        }).bind(this), 1000);
        return;
    }

    if (typeof this.ports !== 'undefined' && this.ports !== null) {
        var props = {};

        for (var prop in CmdSocket.prototype.props) {
            if (!CmdSocket.prototype.props.hasOwnProperty(prop)) continue;
            if (typeof this[prop] !== 'undefined') {
                props[prop] = this[prop];
            }
        }

        this.cmdSocket = new CmdSocket(props);
        this.cmdSocket.connect(this.onCmdSocketConnectResult.bind(this),
            this.onCmdSocketReceiveCommand.bind(this),
            this.onCmdSocketConnectionLost.bind(this),
            this.onCmdSocketSendSync.bind(this));
        initializeHandled = true;
    }

    if (this.webSocketEnable) {
        this.cmdWebSocket = new CmdWebSocket(this.name, this.ipAddress,
            this.webSocketPort, this.webSocketUrlPath, this.webSocketDataFormat);
        this.cmdWebSocket.connect(this.onCmdWebSocketConnectResult.bind(this),
            this.onCmdWebSocketReceiveCommand.bind(this),
            this.onCmdWebSocketConnectionLost);
        initializeHandled = true;
    }

    if (!initializeHandled) { // drivers with only http requests
        this.fsm.initialize();
    }
};

// If a driver implements onInitialize, it must call this function when it is
// finished with the onInitialize behavior.

CmdDevice.prototype.finishInitialization = function() {
    if (this.initializationTimeout !== null) {
        window.clearTimeout(this.initializationTimeout);
        this.initializationTimeout = null;
    }

    this.defer(this.fsm.validate.bind(this.fsm));
};

// If a driver implements onValidate, it must call this function when it is
// finished with the onValidate behavior.

CmdDevice.prototype.finishValidation = function(results, configName) {
    if (typeof configName === 'undefined') configName = this.configName;

    if (this.validationTimeout !== null) {
        window.clearTimeout(this.validationTimeout);
        this.validationTimeout = null;
    }

    if (typeof this.savedConfig !== 'undefined') {
        log.debug(this + ': validating config received while validating ' +
            'previous one');
        this.config = this.savedConfig;
        delete this.savedConfig;
        this.defer(this.onValidating.bind(this));
    } else if (typeof results === 'undefined') {
        this.defer(this.fsm.control.bind(this.fsm));
    } else if (results === null) {
        this.defer(this.fsm.failValidate.bind(this.fsm));
    } else {
        var isValid = this.objIsValid(results);
        log.info(this + ': config is ' + (isValid ? 'valid' : 'invalid') +
            ': ' + JSON.stringify(results, null, 4));

        this.broadcastStatus('configValidated', {
            path: [configName, 'validated'],
            validated: results,
        });

        if (isValid && this.fsm.current !== 'controlling') {
            this.defer(this.fsm.control.bind(this.fsm));
        } else if (!isValid && this.fsm.current !== 'controlFailed') {
            this.defer(this.fsm.failValidate.bind(this.fsm));
        }
    }
};

// Internal function for formatting a command to be displayed in the logs.

CmdDevice.prototype.formatCommand = function(cmd) {
    if (cmd === null) return 'null';

    if (!this.isBinary && cmd.charCodeAt(0) >= 32 && cmd.charCodeAt(0) < 127) {
        return cmd;
    }

    var str = '[';

    for (var n = 0; n < cmd.length; n++) {
        if (n !== 0) str += ' ';
        if (cmd.charCodeAt(n) < 16) str += '0';
        str += (cmd.charCodeAt(n) & 0x00ff).toString(16);
    }

    str += ']';

    return str;
};

// If a driver implements onControl, it must call this function when it is
// finished with the onControl behavior.

CmdDevice.prototype.finishControlling = function() {
    // Compute caps again. Some drivers determine some caps dynamically after
    // they are connected to the device.

    this.computeCaps();
    this.startPinging();
    if (this.pollIntervalMs > 0) this.startPolling();
};

// Convenience function for drivers to generate a database id.

CmdDevice.prototype.generateId = function() {
    return IdGenerator.generateId();
};

// Convenience function for drivers to get a caps object that they can modify.

CmdDevice.prototype.getCapsTemplate = function() {
    return JSON.parse(JSON.stringify(this.capsTemplate));
};

// Utility function for drivers to get a value from within a json object.

CmdDevice.prototype.getNode = sutl.getNode;

// Function for drivers to get the current validator state.

CmdDevice.prototype.getValidateState = function() {
    return this.validateSteps[this.validateStepIndex].state;
};

// Internal function for handling a command received from the device.

CmdDevice.prototype.handleCommand = function(param, cmd) {
    if (param === null) return false;

    if (!this.commands[param].hasOwnProperty('match')) return false;

    if (!this.commandIsActive(param)) return;

    var regexpOrFunc = this.commands[param].match;
    if (regexpOrFunc === null) return false;

    var match;

    if (typeof regexpOrFunc === 'function') {
        match = regexpOrFunc.call(this, cmd);
    } else {
        match = regexpOrFunc.exec(cmd);
    }

    if (match === null) return false;

    if (param === 'ping') {
        this.pingReceived = true;
    }

    if (this.commands[param].hasOwnProperty('process') &&
        this.commands[param].process !== null) {
        this.commands[param].process.call(this, match);
    }

    return true;
};

// Function that tells whether there is a request for the specified parameter
// in the http request send queue.

CmdDevice.prototype.hasRequest = function(param) {
    return this.httpRequestEnable && this.cmdHttpRequest.hasRequest(param);
};

// Function that tells whether the specified synchronous parameter is in the
// send queue (or response queue if respQueue is true).

CmdDevice.prototype.hasSync = function(param, respQueue) {
    return this.cmdSocket.hasSync(param, respQueue);
};

// Internal function for deleting blank properties from an object.

CmdDevice.prototype.objDeleteBlanks = function(obj) {
    for (var key in obj) {
        if (!obj.hasOwnProperty(key)) continue;

        if (typeof obj[key] === 'string') {
            if (obj[key].trim() === '') {
                delete obj[key];
            }
        } else {
            this.objDeleteBlanks(obj[key]);
        }
    }
};

// Internal function for determining if two objects are equal.

CmdDevice.prototype.objEquals = sutl.objEquals;

// Internal function for determining if a configuration is valid by inspecting
// the validation object.

CmdDevice.prototype.objIsValid = function(obj) {
    var isValid = true;

    for (var key in obj) {
        if (!obj.hasOwnProperty(key)) continue;

        if (typeof obj[key] === 'string') {
            isValid = obj[key] === 'ok';
        } else {
            isValid = this.objIsValid(obj[key]);
        }

        if (!isValid) break;
    }

    return isValid;
};

// Internal handler for authFailed state.

CmdDevice.prototype.onAuthFailed = function() {
    this.scheduleConnectRetry();
};

// Internal handler for busy debouncing.

CmdDevice.prototype.onBusyTimeout = function() {
    this.busyTimeout = null;

    if (this.busy !== this.newBusy) {
        this.busy = this.newBusy;
        if (this.fsm.current === 'controlling') {
            this.broadcastStatus('busy', this.busy);
        }
    }
};

// Internal handler for the CmdSocket's onConnectionLost callback.

CmdDevice.prototype.onCmdSocketConnectionLost = function() {
    log.info(this + ': connection lost, attempting to reconnect');
    this.resetEverything();
    this.fsm.connect();
};

// Internal callback for connection results from CmdSocket.

CmdDevice.prototype.onCmdSocketConnectResult = function(result) {
    if (result.connected) {
        if (result.authenticated) {
            if (result.state === 'succeeded') {
                log.info(this + ': successfully connected to port ' + result.port);

                // Set sync timeout overrides for any params that specify them.

                for (var param in this.commands) {
                    if (!this.commands.hasOwnProperty(param)) continue;

                    if (this.commands[param].hasOwnProperty('syncTimeoutMs')) {
                        this.cmdSocket.overrideSyncTimeoutMs(param,
                            this.commands[param].syncTimeoutMs);
                    }

                    if (this.commands[param].hasOwnProperty('onRetry')) {
                        this.cmdSocket.setRetryCallback(param,
                            this.commands[param].onRetry.bind(this),
                            this.commands[param].retryLimit);
                    }
                }

                this.fsm.initialize();
            } else {
                log.info(this + ': unable to connect, verification failed');
                this.fsm.failVerify();
            }
        } else {
            log.info(this + ': unable to connect, authentication failed');
            this.fsm.failAuth();
        }
    } else {
        log.info(this + ': unable to connect, connection failed');
        this.fsm.failConnect();
    }
};

// Internal handler for responses from CmdHttpRequest.

CmdDevice.prototype.onCmdHttpRequestReceiveResponse = function(cmd, param) {
    var resp = null;

    if (this.commands[param].receiveFullResponse) {
        resp = cmd;
    } else if (cmd.status >= 200 && cmd.status <= 299) {
        if (this.cmdHttpRequest.dataFormat === 'json') {
            try {
                resp = cmd.data.length === 0 ? {} : JSON.parse(cmd.data);
            } catch (error) {
                resp = cmd.data;
            }
        } else {
            resp = cmd.data;
        }
    }

    if (this.handleCommand(param, resp)) {
        driverLog.info(this + ': handled with: ' + param);

        this.resumePingAndPolling(param);
    } else {
        driverLog.info(this + ': failed to match ' + param);
    }
};

// Internal handler for the CmdWebSocket's onConnectionLost callback.

CmdDevice.prototype.onCmdWebSocketConnectionLost = function() {
    log.info(this + ': connection lost, attempting to reconnect');
    this.resetEverything();
    this.fsm.connect();
};

// Internal handler for connection results from CmdWebSocket.

CmdDevice.prototype.onCmdWebSocketConnectResult = function(result) {
    if (result.state === 'succeeded') {
        log.info(this + ': successfully connected to ' + this.cmdWebSocket.fullUrl);
        this.fsm.initialize();
    } else {
        log.info(this + ': unable to connect to ' + this.cmdWebSocket.fullUrl);
        this.fsm.failConnect();
    }
};

// Internal handler for CmdSocket's onReceiveCommand callback. For async drivers
// the param argument is undefined. For fully synchronous drivers, the param
// argument will always be specified. For synchronous drivers that support async
// responses, the param argument will be specified if the command was in
// response to a synchronous command, or it will be null if the command was
// received asynchronously.

CmdDevice.prototype.onCmdSocketReceiveCommand = function(cmd, param) {
    var handled = false;

    if (this.isAsync) {
        for (param in this.commands) { // allow multiple commands to match
            if (!this.commands.hasOwnProperty(param)) continue;
            if (this.handleCommand(param, cmd)) {
                handled = true;
            }
        }
    } else if (this.rejectAsync) {
        if (this.handleCommand(param, cmd)) {
            driverLog.info(this + ': handled with ' + param);

            handled = true;
            this.resumePingAndPolling(param);
        } else {
            driverLog.warning(this + ': failed to match ' + param + ': ' +
                this.cmdSocket.formatCommand(cmd));
        }
    } else {
        if (this.handleCommand(param, cmd)) {
            driverLog.info(this + ': handled with ' + param);

            handled = true;
            this.resumePingAndPolling(param);
        } else {
            for (param in this.commands) {
                if (!this.commands.hasOwnProperty(param)) continue;
                if (!this.commands[param].isAsync) continue;
                if (this.handleCommand(param, cmd)) {
                    driverLog.info(this + ': handled asynchronously by ' + param);

                    // Leave handled set to false, since this was handled by
                    // an async handler.
                }
            }
        }
    }

    return handled;
};

CmdDevice.prototype.onCmdWebSocketReceiveCommand = function(cmd) {
    for (var param in this.commands) { // allow multiple commands to match
        if (!this.commands.hasOwnProperty(param)) continue;
        if (this.commands[param].hasOwnProperty('transport') &&
            this.commands[param].transport !== 'webSocket') continue;
        this.handleCommand(param, cmd);
    }
};

CmdDevice.prototype.onCmdSocketSendSync = function(param) {
    if (param !== null && this.commands[param].hasOwnProperty('syncTimeoutMs')) {
        var syncTimeoutMs = this.commands[param].syncTimeoutMs;
        this.suspendPing(syncTimeoutMs + 200);
        this.suspendPolling(syncTimeoutMs + 300);
    }
};

// Internal handler for connectFailed state.

CmdDevice.prototype.onConnectFailed = function() {
    this.scheduleConnectRetry();
};

// Internal timeout handler for retrying a connection.

CmdDevice.prototype.onConnectRetryTimeout = function() {
    log.info(this + ': attempting to reconnect');
    this.connectRetryTimeout = null;
    this.fsm.connect();
};

// Internal handler for connecting state. Drivers may add behavior by
// implementing onConnect(). If they do, they must call finishConnecting()
// when they are done.

CmdDevice.prototype.onConnecting = function() {
    this.resetEverything();

    if (this.httpRequestEnable) {
        this.cmdHttpRequest = new CmdHttpRequest(this.name, this.httpRequestBaseUrl,
            this.httpRequestDataFormat, this.onCmdHttpRequestReceiveResponse.bind(this));
    }

    if (typeof this.onConnect !== 'undefined') {
        this.onConnect();
    } else {
        this.finishConnecting();
    }
};

// Internal handler for control failed state.

CmdDevice.prototype.onControlFailed = function() {
    this.stopPolling();
    if (this.pingTimeout === null) this.startPinging();
};

// Internal handler for controlling state. Drivers may add behavior by
// implementing onControl(). If they do, they must call finishControlling()
// when they are done.

CmdDevice.prototype.onControlling = function() {
    if (typeof this.onControl !== 'undefined') {
        this.onControl();
    } else {
        this.finishControlling();
    }
};

// Internal handler for all states. This handles broadcasting the connectState
// to EquipmentService.

CmdDevice.prototype.onEnterState = function() {
    log.debug(this + ': onEnterState: ' + this.fsm.current);
    var newConnectState = this.connectStateMap[this.fsm.current];

    if (newConnectState === null ||
        newConnectState === this.connectState) return;

    this.connectState = newConnectState;
    if (this.onConnectStatus) this.onConnectStatus(this);
};

// Timeout for when initialization has stalled.

CmdDevice.prototype.onInitializationTimeout = function() {
    log.info(this + ': initialization timeout, attempting to reconnect');
    this.resetEverything();
    this.fsm.connect();
};

// Internal handler for initializing state. Drivers may add behavior by
// implementing onInitialize(). If they do, they must call
// finishInitialization() when they are done.

CmdDevice.prototype.onInitializing = function() {
    if (typeof this.onInitialize !== 'undefined') {
        this.initializationTimeout =
            window.setTimeout(this.onInitializationTimeout.bind(this),
                this.initializationTimeoutMs);
        this.onInitialize();
    } else {
        this.finishInitialization();
    }
};

// Internal handler for ping timeouts.

CmdDevice.prototype.onPingTimeout = function() {
    if (this.pingReceived || this.pingSuspended) {
        this.pingSuspended = false;
        this.pingReceived = false;
        this.pingAttempts = 0;
        this.query('ping');
        this.pingTimeout =
            window.setTimeout(this.onPingTimeout.bind(this), this.pingMs);
    } else {
        this.pingAttempts++;

        if (this.pingAttempts >= this.pingAttemptLimit) {
            log.warning(this + ': did not receive last ping (attempt ' +
                this.pingAttempts + '/' + this.pingAttemptLimit + '); ' +
                'attempting to reconnect');
            this.pingTimeout = null;
            this.reconnect();
        } else {
            log.warning(this + ': did not receive last ping (attempt ' +
                this.pingAttempts + '/' + this.pingAttemptLimit + '); ' +
                'pinging again');
            this.pingReceived = false;
            this.query('ping');
            this.pingTimeout =
                window.setTimeout(this.onPingTimeout.bind(this), this.pingMs);
        }
    }
};

// Internal handler for poll timeouts. It calls the driver-defined onPoll
// function.

CmdDevice.prototype.onPollTimeout = function() {
    if (typeof this.onPoll === 'undefined') {
        log.error(this + ': onPoll function not defined');
        return;
    }

    if (this.cmdSocket !== null && !this.cmdSocket.isFillingUp() ||
        this.cmdWebSocket !== null || this.cmdHttpRequest !== null) {
        this.onPoll();
    }

    this.pollTimeout =
        window.setTimeout(this.onPollTimeout.bind(this), this.pollIntervalMs);
};

// Internal handler for validating state. Drivers may add functionality by
// implementing onValidate(). If they do, they must call finishValidation()
// when they are done.

CmdDevice.prototype.onValidating = function() {
    this.stopPolling();
    this.stopPinging();

    if (this.failOnEmptyConfig &&
        (typeof this.config === 'undefined' ||
         this.config === null ||
         Object.keys(this.config).length === 0)) {

        this.broadcastStatus('configValidated', {
            path: [this.configName, 'validated'],
            validated: {},
        });

        this.defer(this.fsm.failValidate.bind(this.fsm));
        return;
    }

    if (typeof this.onValidate !== 'undefined') {
        if (this.validationTimeoutMs > 0) {
            this.validationTimeout =
                window.setTimeout(this.onValidationTimeout.bind(this),
                    this.validationTimeoutMs);
        }

        this.onValidate();
    } else {
        this.finishValidation();
    }
};

// Timeout for when validatin has stalled.

CmdDevice.prototype.onValidationTimeout = function() {
    log.info(this + ': validation timeout, attempting to reconnect');
    this.resetEverything();
    this.fsm.connect();
};

// Internal handler for verifyFailed state.

CmdDevice.prototype.onVerifyFailed = function() {
    this.scheduleConnectRetry();
};

// Function for EquipmentService to query a parameter.

CmdDevice.prototype.query = function(param) {
    if (this.logVerbose) {
        driverLog.debug(this + ': query: ' + param);
    }

    if (!this.commandIsActive(param)) {
        log.error(this + ': ' + param + ' not active in ' + this.fsm.current + ' state');
        return;
    }

    if (this.commands.hasOwnProperty(param)) {
        if (this.commands[param].hasOwnProperty('get') &&
            this.commands[param].get !== null) {
            this.commands[param].get.call(this);
        } else {
            log.warning(this + ': query: ' + param +
                ' does not have get function');
        }
    } else {
        log.warning(this + ': query: no definition for ' + param);
    }

};

// This method disconnects an active connection and causes the state machine
// to attempt a reconnect.

CmdDevice.prototype.reconnect = function() {
    this.disconnect();
    this.fsm.connect();
};

// This method causes the driver to go back to the initialize state.

CmdDevice.prototype.reinitialize = function() {
    this.stopPinging();
    this.stopPolling();
    this.fsm.initialize();
};

CmdDevice.prototype.removeRequest = function(param) {
    if (this.httpRequestEnable) this.cmdHttpRequest.remove(param);
};

// Internal function to do clean up when destroying, restarting, etc.

CmdDevice.prototype.resetEverything = function() {
    this.stopPinging();
    this.stopPolling();

    for (var i = 0; i < this.deferTimeouts.length; i++) {
        window.clearTimeout(this.deferTimeouts[i]);
    }

    this.deferTimeouts.length = 0;

    if (this.busyTimeout !== null) {
        window.clearTimeout(this.busyTimeout);
        this.busyTimeout = null;
    }

    this.busy = false;

    if (this.connectRetryTimeout !== null) {
        window.clearTimeout(this.connectRetryTimeout);
        this.connectRetryTimeout = null;
    }

    if (this.initializationTimeout !== null) {
        window.clearTimeout(this.initializationTimeout);
        this.initializationTimeout = null;
    }

    if (this.simConnectTimeout !== null) {
        window.clearTimeout(this.simConnectTimeout);
        this.simConnectTimeout = null;
    }

    if (this.suspendSyncTimeout !== null) {
        window.clearTimeout(this.suspendSyncTimeout);
        this.suspendSyncTimeout = null;
    }

    if (this.validationTimeout !== null) {
        window.clearTimeout(this.validationTimeout);
        this.validationTimeout = null;
    }

    if (this.cmdHttpRequest !== null) {
        this.cmdHttpRequest.destroy();
        this.cmdHttpRequest = null;
    }

    if (this.cmdSocket !== null) {
        this.cmdSocket.destroy();
        this.cmdSocket = null;
    }

    if (this.cmdWebSocket !== null) {
        this.cmdWebSocket.destroy();
        this.cmdWebSocket = null;
    }

    this.connectState = 'connectFailed';
};

// Function to allow drivers to reset the initialization timeout. This can be
// used to allow smaller values for initializationTimeoutMs, for example by
// calling resetInitializationTimeout() each time a valid initialization
// command is received.

CmdDevice.prototype.resetInitializationTimeout = function() {
    if (this.initializationTimeout === null) {
        log.error(this + ': initialization timeout not active, can\'t reset');
        return;
    }

    window.clearTimeout(this.initializationTimeout);
    this.initializationTimeout =
        window.setTimeout(this.onInitializationTimeout.bind(this),
            this.initializationTimeoutMs);
};

// Function to allow drivers to reset the validation timeout. This can be used
// to allow smaller values for validationTimeoutMs, for example by calling
// resetValidationTimeout() each time a valid validation command is received.

CmdDevice.prototype.resetValidationTimeout = function() {
    if (this.validationTimeout === null) {
        log.error(this + ': validation timeout not active, can\'t reset');
        return;
    }

    window.clearTimeout(this.validationTimeout);
    this.validationTimeout =
        window.setTimeout(this.onValidationTimeout.bind(this),
            this.validationTimeoutMs);
};

// Internal function to resume pinging and/or polling after they were suspended
// by a command-specific option.

CmdDevice.prototype.resumePingAndPolling = function(param) {
    if (param !== null && this.commands[param].syncTimeoutMs) {
        if (this.commands[param].hasOwnProperty('resumeDelayMs')) {

            if (this.cmdSocket !== null) {
                this.cmdSocket.suspendSync(this.commands[param].resumeDelayMs);
            }

            this.suspendPing(this.commands[param].resumeDelayMs + 100);
            this.suspendPolling(this.commands[param].resumeDelayMs + 200);
        } else {
            this.suspendPing(this.pingMs);
            this.suspendPolling(this.pollIntervalMs);
        }
    }
};

// Internal function to schedule a reconnection attempt.

CmdDevice.prototype.scheduleConnectRetry = function() {
    if (this.connectRetryTimeout !== null) {
        log.error(this + ': scheduleConnectRetry: connectRetryTimeout not null');
        window.clearTimeout(this.connectRetryTimeout);
    }

    this.connectRetryTimeout = window.setTimeout(
        this.onConnectRetryTimeout.bind(this), this.connectRetryMs);
};

CmdDevice.prototype.sendCommand = function(cmdOrParam, cmd, func) {
    if (this.isAsync) {
        if (typeof cmd !== 'undefined') {
            log.error(this + ': sendCommand: param shouldn\'t be specified: ' + cmdOrParam);
            return;
        }
    } else {
        if (typeof cmd === 'undefined') {
            log.error(this + ': sendCommand: synchronous drivers must specify param');
            return;
        }
    }

    if (this.cmdSocket !== null) {
        if (func) {
            this.cmdSocket.sendCommand(cmdOrParam, cmd, func);
        } else {
            this.cmdSocket.sendCommand(cmdOrParam, cmd);
        }
    } else if (this.cmdWebSocket !== null) {
        this.cmdWebSocket.send(cmdOrParam);
    }
};

CmdDevice.prototype.sendRequest = function(param, type, urlPath, data, headers, timeoutMs) {
    if (!this.commands.hasOwnProperty(param)) {
        log.error(this + ': sendRequest: command doesn\'t exist: ' + param);
        return;
    }

    var isAsync =  this.commands[param].hasOwnProperty('isAsync') &&
        this.commands[param].isAsync;
    this.cmdHttpRequest.send(param, type, urlPath, data, headers, isAsync, timeoutMs);
};

// Function for drivers to send detected information to EquipmentService.

CmdDevice.prototype.sendDetected = function(results, configName) {
    if (typeof configName === 'undefined') configName = this.configName;
    log.info(this + ': detected = ' + JSON.stringify(results, null, 4));
    this.broadcastStatus('configDetected', {
        path: [ configName, 'detected' ],
        detected: results,
    });
};

// Function for EquipmentService to set a parameter.

CmdDevice.prototype.set = function(param, value) {
    if (typeof value === 'undefined') {
        log.debug(this + ': set: ' + param);
    } else {
        log.debug(this + ': set: ' + param + ' = ' + JSON.stringify(value));
    }

    if (!this.commandIsActive(param)) {
        log.error(this + ': ' + param + ' not active in ' + this.fsm.current + ' state');
        return;
    }

    if (this.commands.hasOwnProperty(param)) {
        if (this.commands[param].hasOwnProperty('set') &&
            this.commands[param].set !== null) {
            this.commands[param].set.call(this, value);
        } else {
            log.warning(this + ': set: ' + param +
                ' does not have set function');
        }
    } else {
        log.warning(this + ': set: no definition for ' + param);
    }
};

// Debounced implementation of busy flag for drivers that need to implement it.

CmdDevice.prototype.setBusy = function(busy, delayMs) {
    if (typeof delayMs === 'undefined') delayMs = 200;

    if (this.busyTimeout !== null) {
        window.clearTimeout(this.busyTimeout);
    }

    this.newBusy = busy;

    if (delayMs === 0) {
        this.onBusyTimeout();
    } else {
        this.busyTimeout = window.setTimeout(
            this.onBusyTimeout.bind(this), delayMs);
    }
};

// Utility function for drivers to set a value within a json object.

CmdDevice.prototype.setNode = sutl.setNode;

// Function that drivers can use to start the initializer, which is provided for
// simple initializations that proceed in a linear set of steps.

CmdDevice.prototype.startInitializer = function() {
    this.initializerFirstStep = true;
    this.initializeStepIndex = 0;
    this.initializeRepeatIndex = 0;
    this.doNextInitializeStep();
};

// Internal function that starts the processes of pinging the device to make
// sure it is still connected.

CmdDevice.prototype.startPinging = function() {
    if (this.simulate) return; // Don't ping for the simulator.

    this.pingAttempts = 0;
    this.pingReceived = false;
    this.query('ping');

    // It's possible that we got here as a result of the transition from
    // controlFailed to controlling. If that's the case, then the ping timer
    // is already running. So, only start the timer if it isn't already running.

    if (this.pingTimeout === null) {
        this.pingTimeout =
            window.setTimeout(this.onPingTimeout.bind(this), this.pingMs);
    }
};

// Internal function that starts the optional polling process.

CmdDevice.prototype.startPolling = function() {
    if (this.pollTimeout !== null) {
        log.error(this + ': startPolling: pollTimeout not null');
        window.clearTimeout(this.pollTimeout);
    }

    this.pollTimeout =
        window.setTimeout(this.onPollTimeout.bind(this), this.pollIntervalMs);
};

// Function that drivers can use to start the validator, which is provided for
// simple validations that proceed in a linear set of steps.

CmdDevice.prototype.startValidator = function() {
    this.validatorFirstStep = true;
    this.validateStepIndex = 0;
    this.validateRepeatIndex = 0;
    this.doNextValidateStep();
};

// Internal function that stops the pinging process.

CmdDevice.prototype.stopPinging = function() {
    if (this.pingTimeout !== null) {
        window.clearTimeout(this.pingTimeout);
        this.pingTimeout = null;
    }
};

// Internal function that stops the polling process.

CmdDevice.prototype.stopPolling = function() {
    if (this.pollTimeout !== null) {
        window.clearTimeout(this.pollTimeout);
        this.pollTimeout = null;
    }
};

// Function for drivers to suspend pinging and polling for the specified amount
// of time. For synchronous drivers, it also suspends outgoing commands.

CmdDevice.prototype.suspend = function(suspendMs) {
    if (this.cmdSocket !== null && !this.isAsync) this.cmdSocket.suspendSync(suspendMs);
    // TODO: implement suspend for cmdHttpRequest
    this.suspendPing(suspendMs + 100);
    this.suspendPolling(suspendMs + 200);
};

// Internal function that suspends pings for the specified amount of time.

CmdDevice.prototype.suspendPing = function(suspendMs) {
    log.debug(this + ': suspending ping for ' + suspendMs + ' ms');

    if (this.pingTimeout !== null) {
        window.clearTimeout(this.pingTimeout);
    }

    this.pingSuspended = true;
    this.pingAttempts = 0;
    this.pingTimeout =
        window.setTimeout(this.onPingTimeout.bind(this), suspendMs);
};

// Internal function that suspends polling for the specified amount of time.

CmdDevice.prototype.suspendPolling = function(suspendMs) {
    if (typeof this.onPoll === 'undefined') return;

    log.debug(this+ ': suspending polling for ' + suspendMs + ' ms');

    if (this.pollTimeout !== null) {
        window.clearTimeout(this.pollTimeout);
    }

    this.pollTimeout =
        window.setTimeout(this.onPollTimeout.bind(this), suspendMs);
};

// Convenience implementation of toString().

CmdDevice.prototype.toString = function() {
    return this.name + ' ' + this.ipAddress;
};

// Function for EquipmentService to update the driver's configuration.

CmdDevice.prototype.updateConfig = function(config) {
    if (typeof config === 'undefined' || config === null) config = {};

    // Simplify the config structure. Eventually we will refactor the database
    // to remove this complexity, but for now just hide it from the drivers.

    if (typeof this.configName !== 'undefined') {
        var newConfig;

        if (typeof config[this.configName] !== 'undefined' &&
            typeof config[this.configName].manual !== 'undefined') {
            newConfig = config[this.configName].manual;
        } else {
            newConfig = {};
        }

        var copyProps = [ 'name', 'port', 'audioDialingEnabled',
            'airPlayContentEnabled', 'cameraPresets', 'useCalendar',
            'useDirectory', 'videoInputs', 'viscaCameras', 'sourceMenu'
        ];

        copyProps.forEach(function(prop) {
            if (config.hasOwnProperty(prop)) {
                newConfig[prop] = config[prop];
            }
        });

        config = newConfig;
    }

    config = JSON.parse(JSON.stringify(config));

    // Remove any blank properties.

    this.objDeleteBlanks(config);

    // Don't do anything if the config didn't change.

    if (this.objEquals(config, this.config)) {
        log.debug(this + ': config didn\'t change');
        return false;
    }

    log.debug(this + ': config changed');

    if (logVerbose) {
        log.debug(this + ': updateConfig: config = ' +
            JSON.stringify(config, null, 4));
    }

    // If we're in a state where we can't handle it, save it until we can.

    if (this.fsm.current === 'validating') {
        log.debug(this + ': currently validating another config, saving this ' +
            'one for validation afterwards');
        this.savedConfig = config;
        return false;
    }

    // Update the config.

    this.config = config;

    if (typeof this.onUpdateConfig !== 'undefined') this.onUpdateConfig();

    // Ask the driver for caps.

    this.computeCaps();

    // Validate it now if we can.

    if (this.fsm.current === 'controlFailed' ||
        this.fsm.current === 'controlling') {
        this.fsm.validate();
    }

    return true;
};

module.exports = CmdDevice;
